import { Fragment } from 'react';

import { VSpacing, Link } from '@hh.ru/magritte-ui';
import { TranslateGuard } from '@hh.ru/magritte-ui-translate-guard';
import { ChevronRightOutlinedSize16 } from '@hh.ru/magritte-ui/icon';
import { SPALink } from '@hh.ru/redux-spa-middleware';
import { TranslatedComponent } from 'bloko/common/hooks/useTranslations';
import { format } from 'bloko/common/trl';

import useVacancyId from 'src/components/ResumeCard/hooks/useVacancyId';
import { makeResumeHref } from 'src/components/ResumeCard/utils/makeResumeHref';
import translation from 'src/components/translation';
import { useSelector } from 'src/hooks/useSelector';
import useToggleState from 'src/hooks/useToggleState';
import type { ResumeCardData } from 'src/models/resumeCard';

import StaticCollapseTrigger from 'src/components/ResumeCard/Body/Collapse/Trigger/Static';
import Field from 'src/components/ResumeCard/Body/Field';

const TrlKeys = {
    title: 'resume.search.field.linkedResumes',
    resumeEmptyTitle: 'resume.title.empty',
    trigger: 'resumeCard.field.linkedResumes.trigger',
};

const LinkedResumes: TranslatedComponent<{ resume: ResumeCardData }> = ({ resume, trls }) => {
    const highlight = useSelector((state) => state.resumeSearchResult.queries?.highlight);
    const vacancyId = useVacancyId();
    const openInNewWindow = useSelector((state) => state.employerSearchSettings.openInNewWindow);

    const [expanded, toggleExpanded] = useToggleState(false);

    const { linkedResumes } = resume;
    if (!linkedResumes?.length) {
        return null;
    }
    return (
        <Field title={trls[TrlKeys.title]} dataQaName="linked-resumes">
            <StaticCollapseTrigger
                text={format(trls[TrlKeys.trigger], {
                    '{0}': linkedResumes.length,
                })}
                expanded={expanded}
                onToggle={toggleExpanded}
                dataQa="resume-serp_resume-more-resumes"
            />
            {expanded &&
                linkedResumes.map((resume) => (
                    <Fragment key={resume._attributes.hash}>
                        <VSpacing default={8} />
                        <Link
                            Element={SPALink}
                            typography="label-3-regular"
                            iconRight={<ChevronRightOutlinedSize16 />}
                            to={makeResumeHref({
                                id: resume._attributes.id,
                                hash: resume._attributes.hash,
                                simhash: resume._attributes.sim_hash,
                                topicId: resume.topicId,
                                highlight,
                                vacancyId,
                            })}
                            target={openInNewWindow ? '_blank' : '_self'}
                            rel="nofollow"
                            data-qa="resume-serp__resume-another"
                        >
                            {/* тайтла может не быть, если это незавершенное дублированное резюме*/}
                            {resume.title[0]?.string ?? (
                                <TranslateGuard useSpan>{trls[TrlKeys.resumeEmptyTitle]}</TranslateGuard>
                            )}
                        </Link>
                    </Fragment>
                ))}
        </Field>
    );
};

export default translation(LinkedResumes);
